import axios from 'axios';

import { resolve } from '@/api/endPoints';
import {
  UPLOAD_TYPE_OVERLAY_MEDIA,
  UPLOAD_TYPE_PREVIEW_IMAGE
} from '@/config/constants';
import { getMediaMetadata } from '@/redux/slices/adManager/mediaMetadata';
import axiosClient from '@/utils/axios';
import { compressImage } from '@/utils/media/compressImage';

export type OverlayTemplateApiScope =
  | { kind: 'account' }
  | { kind: 'profile'; locationUuid: string };

export type WritableOverlayTemplateApiScope = OverlayTemplateApiScope;

export const getOverlayTemplateScopeKey = (
  scope: OverlayTemplateApiScope
): string =>
  scope.kind === 'account' ? scope.kind : `${scope.kind}:${scope.locationUuid}`;

export type OverlayTemplateOrdering = 'pk' | '-pk' | 'title' | '-title';

export interface PaginatedApiResponse<T> {
  count?: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * What the plan lets this one library hold. The account library and every
 * profile library get their own slots, so this describes the library the list
 * was asked for, not the account.
 *
 * `limit: null` is a plan with no overlay quota at all, and then `used` and
 * `remaining` are null too — the backend does not count a library it can never
 * refuse, and there is nothing for the interface to show.
 *
 * `used` is capped at `limit`. It is exact while there is room left, and reads
 * as the limit itself once the library is full, however far past it actually
 * sits: the backend stops reading where the answer stops mattering, so a large
 * library is not walked on every request. Read `isLimitReached` for "full",
 * never `used > limit`.
 */
export interface OverlayTemplateQuotaDto {
  limit: number | null;
  used: number | null;
  remaining: number | null;
  is_limit_reached: boolean;
}

export interface OverlayTemplateListResponse
  extends PaginatedApiResponse<OverlayTemplateSummaryDto> {
  /** Absent on an older backend, which is read as "no quota". */
  quota?: OverlayTemplateQuotaDto;
}

export interface OverlayTemplateFolderDto {
  uuid: string;
  name: string;
}

export interface OverlayTemplateSummaryDto {
  uuid: string;
  title: string;
  folder_uuid?: string | null;
  thumbnail_url: string;
  aspect_ratio: string;
  is_shared: boolean;
  created: string;
  modified: string;
}

export interface OverlayTemplatePreviewImageDto {
  uuid: string;
  original_asset?: string;
  preview_image?: string;
  thumbnail_image?: string;
  width?: number;
  height?: number;
  [key: string]: unknown;
}

export interface OverlayTemplateDetailDto extends OverlayTemplateSummaryDto {
  configuration: Record<string, unknown>;
  preview_image: OverlayTemplatePreviewImageDto | null;
  is_account_template: boolean;
  location_uuid: string | null;
}

export interface OverlayTemplateWritePayload {
  title: string;
  folder_uuid?: string | null;
  configuration: Record<string, unknown>;
  preview_image_uuid?: string | null;
  aspect_ratio: string;
}

export interface OverlayTemplateListParams {
  scope: OverlayTemplateApiScope;
  isShared?: boolean;
  folderUuid?: string | null;
  search?: string;
  ordering?: OverlayTemplateOrdering;
  limit?: number;
  cursor?: string;
}

export interface OverlayTemplatePreviewUploadResult {
  uuid: string;
  uploaded_object_data?: {
    uuid?: string;
    original_asset?: string;
    preview_image?: string;
    preview_image_url?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export const getOverlayTemplateScopeQuery = (
  scope: OverlayTemplateApiScope
): Record<string, string> => {
  if (scope.kind === 'profile') {
    return { location_uuid: scope.locationUuid };
  }
  return {};
};

const compactQuery = (
  query: Record<string, string | number | null | undefined>
) =>
  Object.fromEntries(
    Object.entries(query).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

export const listOverlayTemplates = async ({
  scope,
  isShared,
  folderUuid,
  search,
  ordering,
  limit,
  cursor
}: OverlayTemplateListParams): Promise<OverlayTemplateListResponse> => {
  const url = resolve(
    'overlay_templates',
    {},
    compactQuery({
      ...getOverlayTemplateScopeQuery(scope),
      is_shared: isShared === undefined ? undefined : String(isShared),
      folder__uuid: folderUuid,
      search,
      ordering,
      limit,
      cursor
    })
  );
  const response = await axiosClient.get<OverlayTemplateListResponse>(url);
  return response.data;
};

/**
 * Follow the opaque URL returned by the cursor-paginated API. Keeping this URL
 * intact avoids rebuilding a next-page request from filters that may have
 * changed since the first page was requested.
 */
export const listOverlayTemplatesPage = async (
  nextUrl: string
): Promise<OverlayTemplateListResponse> => {
  const response = await axiosClient.get<OverlayTemplateListResponse>(nextUrl);
  return response.data;
};

export const getOverlayTemplate = async (
  uuid: string,
  scope: OverlayTemplateApiScope
): Promise<OverlayTemplateDetailDto> => {
  const url = resolve(
    'overlay_template',
    { uuid },
    getOverlayTemplateScopeQuery(scope)
  );
  const response = await axiosClient.get<OverlayTemplateDetailDto>(url);
  return response.data;
};

export const createOverlayTemplate = async (
  payload: OverlayTemplateWritePayload,
  scope: WritableOverlayTemplateApiScope
): Promise<OverlayTemplateDetailDto> => {
  const url = resolve(
    'overlay_templates',
    {},
    getOverlayTemplateScopeQuery(scope)
  );
  const response = await axiosClient.post<OverlayTemplateDetailDto>(
    url,
    payload
  );
  return response.data;
};

export const updateOverlayTemplate = async (
  uuid: string,
  payload: Partial<OverlayTemplateWritePayload>,
  scope: WritableOverlayTemplateApiScope
): Promise<OverlayTemplateDetailDto> => {
  const url = resolve(
    'overlay_template',
    { uuid },
    getOverlayTemplateScopeQuery(scope)
  );
  const response = await axiosClient.patch<OverlayTemplateDetailDto>(
    url,
    payload
  );
  return response.data;
};

export const deleteOverlayTemplate = async (
  uuid: string,
  scope: WritableOverlayTemplateApiScope
): Promise<void> => {
  const url = resolve(
    'overlay_template',
    { uuid },
    getOverlayTemplateScopeQuery(scope)
  );
  await axiosClient.delete(url);
};

export const listOverlayTemplateFolders = async (
  scope: OverlayTemplateApiScope,
  isShared?: boolean,
  nextUrl?: string
): Promise<PaginatedApiResponse<OverlayTemplateFolderDto>> => {
  const url =
    nextUrl ??
    resolve(
      'overlay_template_folders',
      {},
      {
        ...getOverlayTemplateScopeQuery(scope),
        is_shared: isShared === undefined ? undefined : String(isShared)
      }
    );
  const response = await axiosClient.get<
    PaginatedApiResponse<OverlayTemplateFolderDto>
  >(url);
  return response.data;
};

export const createOverlayTemplateFolder = async (
  name: string,
  scope: WritableOverlayTemplateApiScope
): Promise<OverlayTemplateFolderDto> => {
  const url = resolve(
    'overlay_template_folders',
    {},
    getOverlayTemplateScopeQuery(scope)
  );
  const response = await axiosClient.post<OverlayTemplateFolderDto>(url, {
    name
  });
  return response.data;
};

export const updateOverlayTemplateFolder = async (
  uuid: string,
  name: string,
  scope: WritableOverlayTemplateApiScope
): Promise<OverlayTemplateFolderDto> => {
  const url = resolve(
    'overlay_template_folder',
    { uuid },
    getOverlayTemplateScopeQuery(scope)
  );
  const response = await axiosClient.patch<OverlayTemplateFolderDto>(url, {
    name
  });
  return response.data;
};

export const deleteOverlayTemplateFolder = async (
  uuid: string,
  scope: WritableOverlayTemplateApiScope
): Promise<void> => {
  const url = resolve(
    'overlay_template_folder',
    { uuid },
    getOverlayTemplateScopeQuery(scope)
  );
  await axiosClient.delete(url);
};

const uploadOverlayTemplateFile = async (
  {
    file,
    locationUuid,
    onProgress,
    signal
  }: {
    file: File;
    locationUuid?: string;
    onProgress?: (percent: number) => void;
    signal?: AbortSignal;
  },
  uploadType: number
): Promise<OverlayTemplatePreviewUploadResult> => {
  const contentType = file.type || 'image/png';
  const mediaMetadata = await getMediaMetadata(file, contentType);
  const uploadUrl = resolve('create_post_media', {}, {});
  const response = await axiosClient.post<OverlayTemplatePreviewUploadResult>(
    uploadUrl,
    compactQuery({
      content_type: contentType,
      filesize_in_bytes: file.size,
      filename: file.name,
      location_uuid: locationUuid,
      upload_type: uploadType,
      ...mediaMetadata
    }),
    { signal }
  );
  const uploadPreset = response.data;

  await axios.put(String(uploadPreset.url), file, {
    headers: {
      ...((uploadPreset.headers as Record<string, string>) || {}),
      'Content-Type': contentType
    },
    signal,
    onUploadProgress: (event) => {
      const total = event.total || file.size;
      if (!total) return;
      onProgress?.(
        Math.max(0, Math.min(99, Math.round((event.loaded / total) * 100)))
      );
    }
  });
  await axiosClient.put(
    String(uploadPreset.confirm_url),
    {
      success: true
    },
    { signal }
  );

  return uploadPreset;
};

export const uploadOverlayTemplatePreview = async (
  options: Parameters<typeof uploadOverlayTemplateFile>[0]
): Promise<OverlayTemplatePreviewUploadResult> =>
  await uploadOverlayTemplateFile(options, UPLOAD_TYPE_PREVIEW_IMAGE);

/**
 * Uploads an image used inside an overlay configuration and returns its
 * persistent public URL. This deliberately does not use a local blob/data URL:
 * configurations must remain renderable after the editing session ends.
 */
export const uploadOverlayTemplateImage = async (
  options: Parameters<typeof uploadOverlayTemplateFile>[0]
): Promise<{ src: string; uuid: string }> => {
  const file = await compressImage(options.file);
  const upload = await uploadOverlayTemplateFile(
    { ...options, file },
    UPLOAD_TYPE_OVERLAY_MEDIA
  );
  const uploadedImage = upload.uploaded_object_data;
  const url = uploadedImage?.original_asset;
  const uuid = uploadedImage?.uuid ?? upload.uuid;

  if (!url || !uuid) {
    throw new Error(
      'The uploaded image did not include a public URL and UUID.'
    );
  }

  return { src: url, uuid };
};
